import { io, Socket } from 'socket.io-client';
import { refreshAccessToken } from './api';

/** 后端握手鉴权失败时统一抛这个 message（backend/src/services/socket.js） */
const AUTH_ERROR_MESSAGE = 'Authentication error';

class SocketService {
  private socket: Socket | null = null;
  private isConnected = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  /** 因 token 过期而续期重连的次数，连上就清零 */
  private authRetries = 0;
  /** 续期后重连仍被拒就别再试，否则 refresh 成功但 socket 照样被拒会死循环 */
  private readonly maxAuthRetries = 2;
  private isRefreshingAuth = false;

  /**
   * 正在建立的那次连接。
   *
   * 一个页面里 connect() 会被调好几次 —— DispatchConsole 自己调一次，
   * 它用的 useDispatchTasks → useRealtimeTasks 又调一次。两次都发生在
   * 首次连上之前，而原来的短路条件是「已经连上了才复用」，于是第二次
   * 又开了一条 io() 连接：`this.socket` 指向新的那条，旧的那条却还活着，
   * 它的监听照样往同一个单例 emit —— **每条推送都被送两遍**。
   * 屏幕上就是两条一模一样的提示。
   */
  private connectPromise: Promise<void> | null = null;

  // Event listeners
  private listeners: Map<string, ((...args: unknown[]) => void)[]> = new Map();

  connect(token: string): Promise<void> {
    // 已经有一条连接 —— 连上了、还在握手、或者正在自行重连，都算。
    // 一律复用，绝不开第二条。
    if (this.socket) return this.connectPromise ?? Promise.resolve();

    this.connectPromise = new Promise<void>((resolve, reject) => {
      // 握手失败时把这次的 promise 清掉：socket.io 自己还在重连，所以
      // this.socket 留着不动，下次 connect() 会走上面那条复用分支。
      const fail = (err: Error) => { this.connectPromise = null; reject(err); };
      const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || 'http://localhost:4000';

      this.socket = io(SOCKET_URL, {
        // 函数形式：socket.io 每次(重)连握手前都会重新调用它，所以重连时读到的
        // 是 localStorage 里最新的 token。写成 { token } 静态对象的话，拿到的
        // 永远是首次连接那一刻的快照 —— access token 只有 4 小时，超时后任何
        // 一次断线重连都会带着过期 token 去握手，必然被拒。
        // 传进来的 token 只作兜底（localStorage 被清掉时仍能用调用方给的那个）。
        auth: (cb) => cb({ token: localStorage.getItem('token') || token }),
        transports: ['websocket', 'polling'],
        reconnection: true,
        // 不再限制重连次数(使用 socket.io 默认的 Infinity)。原先设成 5 次,
        // 配合 reconnectionDelay 1000 + 默认 delayMax 5000 只坚持约 17 秒,
        // 之后永久放弃 —— 后端部署重启、笔记本休眠、切换网络都远超 17 秒,
        // 于是每次部署后所有开着的标签页实时推送都静默失效, 只能手动刷新。
        // 注意: 这只覆盖传输层断线; 若是握手鉴权失败(token 过期), socket.io
        // 会直接 destroy 且跳过重连, 无限重试对那种情况无效。
        reconnectionDelay: 1000,
      });

      this.socket.on('connect', () => {
        console.log('Connected to Socket.io server');
        this.isConnected = true;
        this.reconnectAttempts = 0;
        this.authRetries = 0;
        resolve();
      });

      this.socket.on('disconnect', (reason) => {
        console.log('Disconnected from Socket.io server:', reason);
        this.isConnected = false;
      });

      this.socket.on('connect_error', (error) => {
        console.error('Socket connection error:', error);

        // 握手鉴权失败要单独处理：socket.io 把中间件错误当成"服务端明确拒绝"，
        // 不会自动重连（上面 reconnection:true 只管传输层断线）。access token
        // 4 小时过期，之后只要断一次线，重连握手就会被拒 —— 若不在这里救回来，
        // 实时推送就此永久失效，而页面看不出任何异常（REST 有自己的 401 续期，
        // 照常返回数据），调度员只会觉得"数据不动了"。
        if (error?.message === AUTH_ERROR_MESSAGE) {
          if (this.isRefreshingAuth) return;

          if (this.authRetries >= this.maxAuthRetries) {
            // 续期成功但仍被拒（账号停用、token_version 被吊销等），
            // 再试也是死循环，交给 REST 的 401 拦截器去跳登录页
            console.error('Socket auth still rejected after token refresh; giving up.');
            fail(new Error('Socket authentication failed'));
            return;
          }

          this.authRetries++;
          this.isRefreshingAuth = true;
          refreshAccessToken()
            .then(() => {
              // 新 token 已写进 localStorage，上面的 auth 函数会在下次握手时读到
              this.socket?.connect();
            })
            .catch(() => {
              // refresh token 也废了。这里不跳登录页 —— 让 REST 的 401 拦截器
              // 统一处理跳转，避免两处抢着跳
              console.error('Token refresh failed; socket will stay disconnected.');
            })
            .finally(() => {
              this.isRefreshingAuth = false;
            });
          return;
        }

        this.reconnectAttempts++;

        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
          fail(new Error('Failed to connect to Socket.io server'));
        }
      });

      this.socket.on('reconnect', (attemptNumber) => {
        console.log(`Reconnected to Socket.io server after ${attemptNumber} attempts`);
        this.isConnected = true;
        this.reconnectAttempts = 0;
      });

      // Set up default event listeners
      this.setupDefaultListeners();
    });

    return this.connectPromise;
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.connectPromise = null;
      this.listeners.clear();
    }
  }

  private setupDefaultListeners(): void {
    if (!this.socket) return;

    // Driver status changes
    this.socket.on('driver_status_changed', (data) => {
      this.emit('driver_status_changed', data);
    });

    // Task updates
    this.socket.on('task_updated', (data) => {
      this.emit('task_updated', data);
    });

    // Task completion
    this.socket.on('task_completed', (data) => {
      this.emit('task_completed', data);
    });

    // Driver location updates
    this.socket.on('driver_location_updated', (data) => {
      this.emit('driver_location_updated', data);
    });

    // Photo uploads
    this.socket.on('photo_uploaded', (data) => {
      this.emit('photo_uploaded', data);
    });

    // Task timeline events (enter/exit terminal, arrive/exit warehouse, etc.)
    this.socket.on('task_timeline_event', (data) => {
      this.emit('task_timeline_event', data);
    });

    // Container status changes
    this.socket.on('container_status_changed', (data) => {
      this.emit('container_status_changed', data);
    });

    // New task created
    this.socket.on('task_created', (data) => {
      this.emit('task_created', data);
    });

    // Driver assigned
    this.socket.on('driver_assigned', (data) => {
      this.emit('driver_assigned', data);
    });

    // Appointment booked
    this.socket.on('appointment_booked', (data) => {
      this.emit('appointment_booked', data);
    });

    // Container released
    this.socket.on('container_released', (data) => {
      this.emit('container_released', data);
    });

    // Return booked
    this.socket.on('return_booked', (data) => {
      this.emit('return_booked', data);
    });

    // Notifications
    this.socket.on('notification', (data) => {
      this.emit('notification', data);
    });

    // Order events (for syncing order list and dispatch page)
    this.socket.on('order_created', (data) => {
      this.emit('order_created', data);
    });
    this.socket.on('order_updated', (data) => {
      this.emit('order_updated', data);
    });
    this.socket.on('shipment_group_created', (data) => {
      this.emit('shipment_group_created', data);
    });
    this.socket.on('shipment_group_deleted', (data) => {
      this.emit('shipment_group_deleted', data);
    });
    this.socket.on('order_deleted', (data) => {
      this.emit('order_deleted', data);
    });

    // Batch task updates
    this.socket.on('tasks_batch_updated', (data) => {
      this.emit('tasks_batch_updated', data);
    });

    // 司机卡里手排的任务顺序变了（别的调度员拖的）
    this.socket.on('driver_task_order_updated', (data) => {
      this.emit('driver_task_order_updated', data);
    });

    // 任务被取消 / 暂停 / 恢复 / 解锁。useRealtimeTasks 一直在订阅这四条，
    // 但它们从没被转出去 —— 结果是别人暂停或取消了一单，你这张看板要等
    // 下一次手动刷新才知道。
    this.socket.on('task_cancelled', (data) => {
      this.emit('task_cancelled', data);
    });
    this.socket.on('task_paused', (data) => {
      this.emit('task_paused', data);
    });
    this.socket.on('task_resumed', (data) => {
      this.emit('task_resumed', data);
    });
    this.socket.on('task_unlocked', (data) => {
      this.emit('task_unlocked', data);
    });

    // ==================== Free Flow ====================
    // 抽屉里那块实时区域整个是哑的：六条推送后端都在发，前端也都在订阅，
    // 中间这一层一条都没转。
    this.socket.on('ff:pool-updated', (data) => {
      this.emit('ff:pool-updated', data);
    });
    this.socket.on('ff:task-created', (data) => {
      this.emit('ff:task-created', data);
    });
    this.socket.on('ff:task-matched', (data) => {
      this.emit('ff:task-matched', data);
    });
    this.socket.on('ff:task-assigned', (data) => {
      this.emit('ff:task-assigned', data);
    });
    this.socket.on('ff:task-picking', (data) => {
      this.emit('ff:task-picking', data);
    });
    this.socket.on('ff:agreement-cancelled', (data) => {
      this.emit('ff:agreement-cancelled', data);
    });

    // ==================== Appointment Events ====================
    this.socket.on('appointment_updated', (data) => {
      this.emit('appointment_updated', data);
    });
    this.socket.on('appointment_cancelled', (data) => {
      this.emit('appointment_cancelled', data);
    });
    this.socket.on('appointments_bulk_updated', (data) => {
      this.emit('appointments_bulk_updated', data);
    });
    // 改车牌 / 改期打开的那张 eModal 页面，对应的预约在码头上已经没了。
    // 这两条流程的成功路走 appointment_updated（一直是通的），但失败路只有
    // 这一条出口，而且它们**没有看门狗** —— 漏转发的后果是转圈永远不停。
    this.socket.on('appointment_link_stale', (data) => {
      this.emit('appointment_link_stale', data);
    });

    // ==================== Extension Booking Events ====================
    // Emitted by backend completeBookingSession after receipt lands cleanly
    // (booking_completed) or when required receipt fields were missing
    // and the flow needs to fall back to the manual modal (booking_incomplete).
    this.socket.on('booking_completed', (data) => {
      this.emit('booking_completed', data);
    });
    this.socket.on('booking_incomplete', (data) => {
      this.emit('booking_incomplete', data);
    });
    // 会话结束但没拿到回执。过了交接点就可能「码头有票、我们没记录」，
    // 这是最不能吞掉的一条。
    this.socket.on('booking_unconfirmed', (data) => {
      this.emit('booking_unconfirmed', data);
    });
    // 一次预约回来不止一张票时的自动配对结果。
    this.socket.on('booking_additional_matched', (data) => {
      this.emit('booking_additional_matched', data);
    });
    this.socket.on('booking_additional_unmatched', (data) => {
      this.emit('booking_additional_unmatched', data);
    });
    // 批量预约的收尾摘要 —— 哪几只柜没订上、哪几只「码头出了票我们没收到」。
    // 漏转发意味着漏订的柜子一声不吭，调度以为整批都成了。
    this.socket.on('batch_booking_summary', (data) => {
      this.emit('batch_booking_summary', data);
    });
    // 「码头有票、我们没收到」复核完了。漏转发的表现是那条「正在自动补取，
    // 先别重约」永远停在屏幕上 —— 票补回来了它不会消失，真没补回来它也不会
    // 升级成「去 My Appointments 手抄」。
    this.socket.on('batch_booking_ticket_status', (data) => {
      this.emit('batch_booking_ticket_status', data);
    });

    // ==================== Gate Fee ====================
    // 闸口费付完的唯一出口：POST 是插件发的，页面没有响应可等。
    // useGateFeeFlow 没有超时兜底，所以漏转发的表现是「正在查询闸口费…」
    // 那条提示永远转下去，同时图标不变灰、发票里的 Port Fee 不出现 ——
    // 而钱其实已经付了、后端也已经记下了。
    this.socket.on('gate_fee_paid', (data) => {
      this.emit('gate_fee_paid', data);
    });

    // eModal 对账读完了。整条链路只有这一个出口 —— POST 是插件的 service
    // worker 发的，调度员那张标签页没有任何响应可等，全靠这条推送收尾。
    // 这一行以前漏在了下面的白名单之外：useEmodalSync 一直在订阅，
    // 而这里从没把原始事件转出去，于是「正在读取你的 eModal 预约」那条
    // 提示只能等 3 分钟看门狗超时，弹窗也就一直挂在屏幕上。
    this.socket.on('emodal_sync_completed', (data) => {
      this.emit('emodal_sync_completed', data);
    });

    // ==================== Chat Events ====================
    this.socket.on('chat_new_message', (data) => {
      this.emit('chat_new_message', data);
    });
    this.socket.on('chat_message_updated', (data) => {
      this.emit('chat_message_updated', data);
    });
    this.socket.on('chat_message_deleted', (data) => {
      this.emit('chat_message_deleted', data);
    });
    this.socket.on('chat_typing', (data) => {
      this.emit('chat_typing', data);
    });
    this.socket.on('chat_read_receipt', (data) => {
      this.emit('chat_read_receipt', data);
    });
    this.socket.on('chat_member_added', (data) => {
      this.emit('chat_member_added', data);
    });
    this.socket.on('chat_member_removed', (data) => {
      this.emit('chat_member_removed', data);
    });
    this.socket.on('chat_group_updated', (data) => {
      this.emit('chat_group_updated', data);
    });
    this.socket.on('chat_total_unread', (data) => {
      this.emit('chat_total_unread', data);
    });
    this.socket.on('chat_conversation_created', (data) => {
      this.emit('chat_conversation_created', data);
    });
    this.socket.on('chat_added_to_group', (data) => {
      this.emit('chat_added_to_group', data);
    });

    // ==================== Driver Company Events ====================
    this.socket.on('driver_joined_company', (data) => {
      this.emit('driver_joined_company', data);
    });
    this.socket.on('driver_left_company', (data) => {
      this.emit('driver_left_company', data);
    });
    this.socket.on('driver_removed', (data) => {
      this.emit('driver_removed', data);
    });

    // ==================== Presence Events ====================
    this.socket.on('online_users', (data) => {
      this.emit('online_users', data);
    });
    this.socket.on('user_presence', (data) => {
      this.emit('user_presence', data);
    });

    // ==================== Email Events ====================
    this.socket.on('email:new_messages', (data) => {
      this.emit('email:new_messages', data);
    });
    this.socket.on('email:thread_updated', (data) => {
      this.emit('email:thread_updated', data);
    });

    // ==================== Reminder Events ====================
    this.socket.on('reminder:created', (data) => {
      this.emit('reminder:created', data);
    });
    this.socket.on('reminder:updated', (data) => {
      this.emit('reminder:updated', data);
    });
    this.socket.on('reminder:completed', (data) => {
      this.emit('reminder:completed', data);
    });
    this.socket.on('reminder:deleted', (data) => {
      this.emit('reminder:deleted', data);
    });
    this.socket.on('reminder:due_notification', (data) => {
      this.emit('reminder:due_notification', data);
    });
  }

  // Event listener management
  on(event: string, callback: (...args: unknown[]) => void): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event)!.push(callback);
  }

  off(event: string, callback?: (...args: unknown[]) => void): void {
    if (!callback) {
      this.listeners.delete(event);
    } else {
      const callbacks = this.listeners.get(event);
      if (callbacks) {
        const index = callbacks.indexOf(callback);
        if (index > -1) {
          callbacks.splice(index, 1);
        }
      }
    }
  }

  private emit(event: string, data: unknown): void {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in event listener for ${event}:`, error);
        }
      });
    }
  }

  // Emit events to server
  emitDriverStatusUpdate(data: {
    driverId: number;
    status: 'On' | 'Off';
    location?: { latitude: number; longitude: number };
  }): void {
    if (this.socket && this.isConnected) {
      this.socket.emit('driver_status_update', data);
    }
  }

  emitTaskUpdate(data: {
    taskId: number;
    status: string;
    notes?: string;
    location?: { latitude: number; longitude: number };
  }): void {
    if (this.socket && this.isConnected) {
      this.socket.emit('task_update', data);
    }
  }

  emitLocationUpdate(data: {
    driverId: number;
    latitude: number;
    longitude: number;
    speed?: number;
    heading?: number;
  }): void {
    if (this.socket && this.isConnected) {
      this.socket.emit('location_update', data);
    }
  }

  emitPhotoUpload(data: {
    taskId: number;
    photoType: string;
    photoUrl: string;
  }): void {
    if (this.socket && this.isConnected) {
      this.socket.emit('photo_uploaded', data);
    }
  }

  emitContainerStatusUpdate(data: {
    containerId: number;
    status: string;
    terminal?: string;
    notes?: string;
  }): void {
    if (this.socket && this.isConnected) {
      this.socket.emit('container_status_update', data);
    }
  }

  // ==================== Chat Methods ====================
  joinChat(conversationId: number): void {
    if (this.socket && this.isConnected) {
      this.socket.emit('join_chat', { conversationId });
    }
  }

  leaveChat(conversationId: number): void {
    if (this.socket && this.isConnected) {
      this.socket.emit('leave_chat', { conversationId });
    }
  }

  emitTyping(conversationId: number): void {
    if (this.socket && this.isConnected) {
      this.socket.emit('chat_typing', { conversationId });
    }
  }

  emitMessageRead(conversationId: number, messageId: number): void {
    if (this.socket && this.isConnected) {
      this.socket.emit('chat_message_read', { conversationId, messageId });
    }
  }

  // Utility methods
  isSocketConnected(): boolean {
    return this.isConnected && this.socket !== null;
  }

  getSocketId(): string | null {
    return this.socket?.id || null;
  }
}

// Export singleton instance
export const socketService = new SocketService(); 